import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { SingleConditionData } from 'services/conditions/useConditionMetrics';
import useGateAnalytics from 'views/Results/Conditions/hooks/useGateAnalytics';
import { secondsSince } from 'views/Results/Conditions/utils/gateAnalytics';

type Gate2TrackingParams = {
  open: boolean;
  acknowledged: boolean;
  condition: SingleConditionData;
};

export default function useGate2Tracking({
  open,
  acknowledged,
  condition,
}: Gate2TrackingParams) {
  const trackGateEvent = useGateAnalytics('gate_2');
  const trackRef = useRef(trackGateEvent);
  const conditionRef = useRef(condition);
  const acknowledgedConditionId = useRef<number | null>(null);
  const dismissed = useRef(false);

  useLayoutEffect(() => {
    trackRef.current = trackGateEvent;
    conditionRef.current = condition;
    acknowledgedConditionId.current = acknowledged
      ? condition.condition_id
      : null;
  }, [trackGateEvent, condition, acknowledged]);

  useEffect(() => {
    if (!open) return;

    const viewedAt = Date.now();
    const conditionProps = {
      condition_id: conditionRef.current.condition_id,
      condition: conditionRef.current.condition,
    };
    dismissed.current = false;

    trackRef.current('Gate 2 Viewed', {
      ...conditionProps,
      marked_in_survey: conditionRef.current.marked_in_survey,
    });

    return () => {
      if (acknowledgedConditionId.current === conditionProps.condition_id) {
        trackRef.current('Gate 2 Acknowledged', {
          ...conditionProps,
          seconds_to_acknowledge: secondsSince(viewedAt),
        });
      } else if (!dismissed.current) {
        trackRef.current('Gate 2 Backed Out', {
          ...conditionProps,
          seconds_on_gate: secondsSince(viewedAt),
        });
      }
    };
  }, [open, condition.condition_id]);

  const markDismissed = useCallback(() => {
    dismissed.current = true;
  }, []);

  return { markDismissed };
}
